import { CheckIcon } from '@heroicons/react/24/outline';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import {
  ActivityColorIcon,
  ChallengeColorIcon,
  CursorColorIcon,
  ResourceColorIcon,
  StudentBookColorIcon,
  TeacherBookColorIcon,
} from '@/components/icons';
import AstroImage from '@/components/ui/custom/AstroImage';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import PerformanceRadial from '@/components/shared/PerformanceRadial';
import { Badge } from '@/components/ui/daisy';
import getLessonItemBadgeLabel from '@/utils/lesson-progress/getLessonItemBadgeLabel';
import { LessonItemType } from '@/utils/lesson/lessonItems';

import {
  LessonProgressCardBannerProps,
  LessonProgressCardDescriptionProps,
  LessonProgressCardItemProps,
  LessonProgressCardItemsProps,
  LessonProgressCardProgressProps,
  LessonProgressCardProps,
  LessonProgressCardTitleProps,
} from './types';

const itemIcons: Record<LessonItemType, ReactNode> = {
  instruction: <CursorColorIcon className="size-6" />,
  material: <StudentBookColorIcon className="size-6" />,
  teacherBook: <TeacherBookColorIcon className="size-6" />,
  activity: <ActivityColorIcon className="size-6" />,
  challenge: <ChallengeColorIcon className="size-6" />,
  resources: <ResourceColorIcon className="size-6" />,
};

const gridAreas = twMerge(
  "[grid-template-areas:'banner_title_progress'_'description_description_description'_'items_items_items']",
  'grid-cols-[auto_minmax(0,1fr)_auto]',
  "md:[grid-template-areas:'progress_banner_title_items'_'progress_banner_description_items']",
  'md:grid-cols-[auto_auto_minmax(0,1fr)_auto]',
);

const useLessonCardTranslation = () =>
  useTranslation('translation', { keyPrefix: 'courseLessons.lessonCard' });

const Root = ({
  className,
  highlighted = false,
  children,
  ...props
}: LessonProgressCardProps) => (
  <article
    className={twMerge(
      'relative grid items-center gap-y-4 rounded-2xl border border-transparent bg-base-300 p-4 md:gap-y-1',
      gridAreas,
      highlighted && 'border-warning',
      className,
    )}
    {...props}
  >
    {children}
  </article>
);

const Progress = ({ progress, className }: LessonProgressCardProgressProps) => {
  const { t } = useLessonCardTranslation();

  return (
    <div
      className={twMerge(
        '[grid-area:progress] ml-4 flex shrink-0 justify-end md:ml-0 md:mr-6',
        className,
      )}
    >
      <ConditionalRenderer
        condition={progress > 0}
        fallback={
          <Badge size="sm" variant="soft" color="neutral">
            {t('notStarted')}
          </Badge>
        }
      >
        <PerformanceRadial performance={progress} label={t('progressLabel')} />
      </ConditionalRenderer>
    </div>
  );
};

const Banner = ({ src, className }: LessonProgressCardBannerProps) => {
  if (!src) return null;

  return (
    <AstroImage
      src={src}
      alt=""
      className={twMerge(
        '[grid-area:banner] mr-4 size-10 shrink-0 rounded-lg object-cover',
        className,
      )}
    />
  );
};

const Title = ({ href, className, children }: LessonProgressCardTitleProps) => (
  <h3
    className={twMerge(
      '[grid-area:title] line-clamp-2 break-words text-sm font-semibold text-base-content md:line-clamp-1 md:self-end',
      className,
    )}
  >
    <ConditionalWrapper
      condition={!!href}
      wrapper={
        <Link
          to={href ?? ''}
          className="after:absolute after:inset-0 after:rounded-2xl after:content-['']"
        />
      }
    >
      {children}
    </ConditionalWrapper>
  </h3>
);

const Description = ({
  className,
  children,
}: LessonProgressCardDescriptionProps) => (
  <p
    className={twMerge(
      '[grid-area:description] font-normal lg:font-semibold line-clamp-2 break-words text-xs text-base-content/50 md:line-clamp-1 md:self-start',
      className,
    )}
  >
    {children}
  </p>
);

const Items = ({ className, children }: LessonProgressCardItemsProps) => (
  <ul
    className={twMerge(
      '[grid-area:items] relative flex items-start justify-start gap-6 md:ml-6 md:w-80 md:shrink-0 md:justify-end',
      className,
    )}
  >
    {children}
  </ul>
);

const Item = ({ className, ...item }: LessonProgressCardItemProps) => {
  const { type, status, href } = item;
  const { t } = useLessonCardTranslation();
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const isDone = status === 'done';
  const badgeLabel = getLessonItemBadgeLabel(item, tCommon);

  return (
    <li className={className}>
      <Link
        to={href}
        className="flex w-10 flex-col-reverse items-center gap-1 text-center"
      >
        <span className="whitespace-nowrap text-[10px] text-base-content/50">
          {t(`items.${type}`)}
        </span>
        <span className="relative flex size-10 items-center justify-center rounded-[10px] bg-accent/10">
          {itemIcons[type]}
          <ConditionalRenderer condition={isDone}>
            <span
              data-testid="lessonItemCheck"
              className="absolute inset-0 flex items-center justify-center rounded-[10px] bg-success/75"
            >
              <CheckIcon className="size-3.5 stroke-[3] text-white" />
            </span>
          </ConditionalRenderer>
          <ConditionalRenderer condition={!!badgeLabel}>
            <Badge
              size="xs"
              color="warning"
              className="absolute -top-1 -right-1 px-1 font-medium text-base-content"
            >
              {badgeLabel}
            </Badge>
          </ConditionalRenderer>
        </span>
      </Link>
    </li>
  );
};

Root.displayName = 'LessonProgressCard';
Progress.displayName = 'LessonProgressCard.Progress';
Banner.displayName = 'LessonProgressCard.Banner';
Title.displayName = 'LessonProgressCard.Title';
Description.displayName = 'LessonProgressCard.Description';
Items.displayName = 'LessonProgressCard.Items';
Item.displayName = 'LessonProgressCard.Item';

export const LessonProgressCard = Object.assign(Root, {
  Progress,
  Banner,
  Title,
  Description,
  Items,
  Item,
});
